import type {
	OrderStatus,
	OriginStatusCategory,
	PaymentState,
	RelationQuery,
	RelationState,
	SenderPriorityUsage,
	SenderStatus,
	TransferRecordState,
} from "@/api/custom_types";
import { useStatusMap } from "@/hooks";
import type { TranslationF, TranslationKey } from "./type-utils";

export type Color = "success" | "primary" | "error" | "warning";

export type StatusTranslation = {
	text: TranslationKey;
	helper?: TranslationKey;
	group: string;
	color: Color;
};

const categoryColors: Record<OriginStatusCategory, Color> = {
	ERROR: "error",
	INFO: "primary",
	SUCCESS: "success",
	WARNING: "warning",
};

const orderStatusConfig = {
	WRONG_EMAIL_PASSWORD: {
		text: "enums.order_status.wrong_email_password",
		helper: "enums.order_status_helper.wrong_email_password",
		group: "status.groupFailed",
		color: "error",
	},
	CAPTCHA_SOLVER_FAILED: {
		text: "enums.order_status.captcha_solver_failed",
		helper: "enums.order_status_helper.captcha_solver_failed",
		group: "status.groupFailed",
		color: "error",
	},
	CLUB_SETUP_REQUIRED: {
		text: "enums.order_status.club_setup_required",
		helper: "enums.order_status_helper.club_setup_required",
		group: "status.groupFailed",
		color: "error",
	},
	CONSOLE_NOT_SUPPORTED: {
		text: "enums.order_status.console_not_supported",
		helper: "enums.order_status_helper.console_not_supported",
		group: "status.groupFailed",
		color: "error",
	},
	CONSOLE_ONLINE: {
		text: "enums.order_status.console_online",
		helper: "enums.order_status_helper.console_online",
		group: "status.groupFailed",
		color: "error",
	},
	DEVICE_SUSPENDED: {
		text: "enums.order_status.device_suspended",
		helper: "enums.order_status_helper.device_suspended",
		group: "status.groupFailed",
		color: "error",
	},
	EA_SERVER_ERROR: {
		text: "enums.order_status.ea_server_error",
		helper: "enums.order_status_helper.ea_server_error",
		group: "status.groupFailed",
		color: "error",
	},
	IN_QUEUE: {
		text: "enums.order_status.in_queue",
		helper: "enums.order_status_helper.in_queue",
		group: "status.groupProcessing",
		color: "success",
	},
	LOGIN_LIMIT_REACHED: {
		text: "enums.order_status.login_limit_reached",
		helper: "enums.order_status_helper.login_limit_reached",
		group: "status.groupFailed",
		color: "error",
	},
	NO_SENDER: {
		text: "enums.order_status.no_sender",
		helper: "enums.order_status_helper.no_sender",
		group: "status.groupFailed",
		color: "error",
	},
	NO_AUTH_PROVIDED: {
		text: "enums.order_status.no_auth_provided",
		helper: "enums.order_status_helper.no_auth_provided",
		group: "status.groupFailed",
		color: "error",
	},
	NO_CLUB: {
		text: "enums.order_status.no_club",
		helper: "enums.order_status_helper.no_club",
		group: "status.groupFailed",
		color: "error",
	},
	NO_PROXY_AVAILABLE: {
		text: "enums.order_status.no_proxy_available",
		helper: "enums.order_status_helper.no_proxy_available",
		group: "status.groupFailed",
		color: "error",
	},
	NO_TM_UNLOCKED_YET: {
		text: "enums.order_status.no_tm_unlocked_yet",
		helper: "enums.order_status_helper.no_tm_unlocked_yet",
		group: "status.groupFailed",
		color: "error",
	},
	ONLINE_IN_WEBAPP: {
		text: "enums.order_status.online_in_webapp",
		helper: "enums.order_status_helper.online_in_webapp",
		group: "status.groupFailed",
		color: "error",
	},
	ONLY_WEB_TM_LOCKED: {
		text: "enums.order_status.only_web_tm_locked",
		helper: "enums.order_status_helper.only_web_tm_locked",
		group: "status.groupFailed",
		color: "error",
	},
	NO_SUITABLE_TRANSFER: {
		text: "enums.order_status.no_suitable_transfer",
		helper: "enums.order_status_helper.no_suitable_transfer",
		group: "status.groupFailed",
		color: "error",
	},
	UNEXPECTED_ERROR: {
		text: "enums.order_status.unexpected_error",
		helper: "enums.order_status_helper.unexpected_error",
		group: "status.groupFailed",
		color: "error",
	},
	ORDER_FINISHED: {
		text: "enums.order_status.order_finished",
		helper: "enums.order_status_helper.order_finished",
		group: "status.groupCompleted",
		color: "success",
	},
	NOT_ENOUGH_COIN: {
		text: "enums.order_status.not_enough_coin",
		helper: "enums.order_status_helper.not_enough_coin",
		group: "status.groupFailed",
		color: "error",
	},
	ORIGIN_DISABLED: {
		text: "enums.order_status.origin_disabled",
		helper: "enums.order_status_helper.origin_disabled",
		group: "status.groupFailed",
		color: "error",
	},
	ORIGIN_PASSWORD_RESET: {
		text: "enums.order_status.origin_password_reset",
		helper: "enums.order_status_helper.origin_password_reset",
		group: "status.groupFailed",
		color: "error",
	},
	PROCESSING: {
		text: "enums.order_status.processing",
		helper: "enums.order_status_helper.processing",
		group: "status.groupProcessing",
		color: "primary",
	},
	PAUSED_TEMPORARILY_FOR_SAFETY: {
		text: "enums.order_status.paused_temporarily_for_safety",
		helper: "enums.order_status_helper.paused_temporarily_for_safety",
		group: "status.groupStopped",
		color: "warning",
	},
	PROXY_NOT_WORKING: {
		text: "enums.order_status.proxy_not_working",
		helper: "enums.order_status_helper.proxy_not_working",
		group: "status.groupFailed",
		color: "error",
	},
	REQUESTED_TO_BE_STOPPED: {
		text: "enums.order_status.requested_to_be_stopped",
		helper: "enums.order_status_helper.requested_to_be_stopped",
		group: "status.groupStopped",
		color: "warning",
	},
	SKIPPED_WAITING_PLAYER: {
		text: "enums.order_status.skipped_waiting_player",
		helper: "enums.order_status_helper.skipped_waiting_player",
		group: "status.groupFailed",
		color: "error",
	},
	TEMP_BANNED: {
		text: "enums.order_status.temp_banned",
		helper: "enums.order_status_helper.temp_banned",
		group: "status.groupFailed",
		color: "error",
	},
	TM_LOCKED: {
		text: "enums.order_status.tm_locked",
		helper: "enums.order_status_helper.tm_locked",
		group: "status.groupFailed",
		color: "error",
	},
	TM_CHECKING_FAILED: {
		text: "enums.order_status.tm_checking_failed",
		helper: "enums.order_status_helper.tm_checking_failed",
		group: "status.groupFailed",
		color: "error",
	},
	TM_LIST_FULL: {
		text: "enums.order_status.tm_list_full",
		helper: "enums.order_status_helper.tm_list_full",
		group: "status.groupFailed",
		color: "error",
	},
	WATCHLIST_FULL: {
		text: "enums.order_status.watchlist_full",
		helper: "enums.order_status_helper.watchlist_full",
		group: "status.groupFailed",
		color: "error",
	},
	WRONG_BACKUP_FORMAT: {
		text: "enums.order_status.wrong_backup_format",
		helper: "enums.order_status_helper.wrong_backup_format",
		group: "status.groupFailed",
		color: "error",
	},
	WRONG_CODE: {
		text: "enums.order_status.wrong_code",
		helper: "enums.order_status_helper.wrong_code",
		group: "status.groupFailed",
		color: "error",
	},
	WRONG_GAUTH_FORMAT: {
		text: "enums.order_status.wrong_gauth_format",
		helper: "enums.order_status_helper.wrong_gauth_format",
		group: "status.groupFailed",
		color: "error",
	},
	STOPPED: {
		text: "enums.order_status.stopped",
		helper: "enums.order_status_helper.stopped",
		group: "status.groupStopped",
		color: "warning",
	},
	EARLY_ACCESS_EXPIRED: {
		text: "enums.order_status.early_access_expired",
		helper: "enums.order_status_helper.early_access_expired",
		group: "status.groupFailed",
		color: "error",
	},
	EA_SERVER_BROKEN: {
		text: "enums.order_status.ea_server_broken",
		helper: "enums.order_status_helper.ea_server_broken",
		group: "status.groupFailed",
		color: "error",
	},
	LOGIN_FAILED: {
		text: "enums.order_status.login_failed",
		helper: "enums.order_status_helper.login_failed",
		group: "status.groupFailed",
		color: "error",
	},
	FAILED_TO_FETCH_SID: {
		text: "enums.order_status.failed_to_fetch_sid",
		helper: "enums.order_status_helper.failed_to_fetch_sid",
		group: "status.groupFailed",
		color: "error",
	},
	TWO_FA_BLOCKED: {
		text: "enums.order_status.two_fa_blocked",
		helper: "enums.order_status_helper.two_fa_blocked",
		group: "status.groupFailed",
		color: "error",
	},
	REFUNDED: {
		text: "enums.order_status.refunded",
		helper: "enums.order_status_helper.refunded",
		group: "status.groupProcessing",
		color: "primary",
	},
	WAITING_FOR_PLAYERS_TO_BE_SOLD: {
		text: "enums.order_status.waiting_for_players_to_be_sold",
		helper: "enums.order_status_helper.waiting_for_players_to_be_sold",
		group: "status.groupProcessing",
		color: "primary",
	},
	AWAITING_END_CUSTOMER: {
		text: "enums.order_status.awaiting_end_customer",
		helper: "enums.order_status_helper.awaiting_end_customer",
		group: "status.groupStopped",
		color: "warning",
	},
} as const satisfies Record<OrderStatus, StatusTranslation>;

const transferRecordStatusConfig = {
	IN_PROGRESS: {
		text: "enums.transfer_record_status.in_progress",
		helper: "enums.transfer_record_status_helper.in_progress",
		group: "status.groupProcessing",
		color: "primary",
	},
	FAIL_TO_EXECUTE: {
		text: "enums.transfer_record_status.fail_to_execute",
		helper: "enums.transfer_record_status_helper.fail_to_execute",
		group: "status.groupFailed",
		color: "error",
	},
	BOUGHT_BUT_NOT_LISTED: {
		text: "enums.transfer_record_status.bought_but_not_listed",
		helper: "enums.transfer_record_status_helper.bought_but_not_listed",
		group: "status.groupFailed",
		color: "primary",
	},
	MISSED: {
		text: "enums.transfer_record_status.missed",
		helper: "enums.transfer_record_status_helper.missed",
		group: "status.groupFailed",
		color: "error",
	},
	SUCCESS: {
		text: "enums.transfer_record_status.success",
		helper: "enums.transfer_record_status_helper.success",
		group: "status.groupCompleted",
		color: "success",
	},
	SNIPED_BUT_NOT_SOLD: {
		text: "enums.transfer_record_status.success_not_sold",
		helper: "enums.transfer_record_status_helper.success_not_sold",
		group: "status.groupCompleted",
		color: "success",
	},
} as const satisfies Record<TransferRecordState, StatusTranslation>;

const senderStatusConfig = {
	WRONG_EMAIL_PASSWORD: {
		text: "enums.sender_status.wrong_email_password",
		group: "status.groupFailed",
		color: "error",
	},
	CAPTCHA_SOLVER_FAILED: {
		text: "enums.sender_status.captcha_solver_failed",
		group: "status.groupFailed",
		color: "error",
	},
	CLUB_SETUP_REQUIRED: {
		text: "enums.sender_status.club_setup_required",
		group: "status.groupFailed",
		color: "error",
	},
	CONSOLE_NOT_SUPPORTED: {
		text: "enums.sender_status.console_not_supported",
		group: "status.groupFailed",
		color: "error",
	},
	CONSOLE_ONLINE: {
		text: "enums.sender_status.console_online",
		group: "status.groupFailed",
		color: "error",
	},
	DEVICE_SUSPENDED: {
		text: "enums.sender_status.device_suspended",
		group: "status.groupFailed",
		color: "error",
	},
	EA_SERVER_ERROR: {
		text: "enums.sender_status.ea_server_error",
		group: "status.groupFailed",
		color: "error",
	},
	CLEANING_TM: {
		text: "enums.sender_status.cleaning_tm",
		group: "status.groupProcessing",
		color: "primary",
	},
	IN_QUEUE: {
		text: "enums.sender_status.in_queue",
		group: "status.groupProcessing",
		color: "success",
	},
	LOGIN_LIMIT_REACHED: {
		text: "enums.sender_status.login_limit_reached",
		group: "status.groupFailed",
		color: "error",
	},
	READY: {
		text: "enums.sender_status.ready",
		group: "status.groupProcessing",
		color: "primary",
	},
	NO_AUTH_PROVIDED: {
		text: "enums.sender_status.no_auth_provided",
		group: "status.groupFailed",
		color: "error",
	},
	NO_CLUB: {
		text: "enums.sender_status.no_club",
		group: "status.groupFailed",
		color: "error",
	},
	NO_PROXY_AVAILABLE: {
		text: "enums.sender_status.no_proxy_available",
		group: "status.groupFailed",
		color: "error",
	},
	NO_TM_UNLOCKED_YET: {
		text: "enums.sender_status.no_tm_unlocked_yet",
		group: "status.groupFailed",
		color: "error",
	},
	ONLINE_IN_WEBAPP: {
		text: "enums.sender_status.online_in_webapp",
		group: "status.groupFailed",
		color: "error",
	},
	ONLY_WEB_TM_LOCKED: {
		text: "enums.sender_status.only_web_tm_locked",
		group: "status.groupFailed",
		color: "error",
	},
	UNEXPECTED_ERROR: {
		text: "enums.sender_status.unexpected_error",
		group: "status.groupFailed",
		color: "error",
	},
	NOT_ENOUGH_COIN: {
		text: "enums.sender_status.not_enough_coin",
		group: "status.groupFailed",
		color: "error",
	},
	SLEEP: {
		text: "enums.sender_status.sleep",
		group: "status.groupFailed",
		color: "error",
	},
	ORIGIN_DISABLED: {
		text: "enums.sender_status.origin_disabled",
		group: "status.groupFailed",
		color: "error",
	},
	ORIGIN_PASSWORD_RESET: {
		text: "enums.sender_status.origin_password_reset",
		group: "status.groupFailed",
		color: "error",
	},
	PROCESSING: {
		text: "enums.sender_status.processing",
		group: "status.groupProcessing",
		color: "primary",
	},
	PROXY_NOT_WORKING: {
		text: "enums.sender_status.proxy_not_working",
		group: "status.groupFailed",
		color: "error",
	},
	TRADING: {
		text: "enums.sender_status.trading",
		group: "status.groupStopped",
		color: "warning",
	},
	TEMP_BANNED: {
		text: "enums.sender_status.temp_banned",
		group: "status.groupFailed",
		color: "error",
	},
	TM_LOCKED: {
		text: "enums.sender_status.tm_locked",
		group: "status.groupFailed",
		color: "error",
	},
	TM_LIST_FULL: {
		text: "enums.sender_status.tm_list_full",
		group: "status.groupFailed",
		color: "error",
	},
	WATCHLIST_FULL: {
		text: "enums.sender_status.watchlist_full",
		group: "status.groupFailed",
		color: "error",
	},
	WRONG_BACKUP_FORMAT: {
		text: "enums.sender_status.wrong_backup_format",
		group: "status.groupFailed",
		color: "error",
	},
	WRONG_CODE: {
		text: "enums.sender_status.wrong_code",
		group: "status.groupFailed",
		color: "error",
	},
	WRONG_GAUTH_FORMAT: {
		text: "enums.sender_status.wrong_gauth_format",
		group: "status.groupFailed",
		color: "error",
	},
	STOPPED: {
		text: "enums.sender_status.stopped",
		group: "status.groupStopped",
		color: "warning",
	},
	EARLY_ACCESS_EXPIRED: {
		text: "enums.order_status.early_access_expired",
		group: "status.groupFailed",
		color: "error",
	},
	EA_SERVER_BROKEN: {
		text: "enums.order_status.ea_server_broken",
		group: "status.groupFailed",
		color: "error",
	},
	LOGIN_FAILED: {
		text: "enums.order_status.login_failed",
		group: "status.groupFailed",
		color: "error",
	},
	FAILED_TO_FETCH_SID: {
		text: "enums.order_status.failed_to_fetch_sid",
		group: "status.groupFailed",
		color: "error",
	},
	TWO_FA_BLOCKED: {
		text: "enums.sender_status.two_fa_blocked",
		group: "status.groupFailed",
		color: "error",
	},
} as const satisfies Record<SenderStatus, StatusTranslation>;

const relationQueryConfig = {
	ACCEPTED: {
		text: "enums.relation_query.accepted",
		group: "status.groupCompleted",
		color: "success",
	},
	ACTIVE: {
		text: "enums.relation_query.active",
		group: "status.groupCompleted",
		color: "primary",
	},
	BANNED: {
		text: "enums.relation_query.banned",
		group: "status.groupFailed",
		color: "error",
	},
	EXPIRED: {
		text: "enums.relation_query.expired",
		group: "status.groupFailed",
		color: "error",
	},
	PENDING: {
		text: "enums.relation_query.pending",
		group: "status.groupProcessing",
		color: "warning",
	},
	REJECTED: {
		text: "enums.relation_query.rejected",
		group: "status.groupFailed",
		color: "error",
	},
} as const satisfies Record<RelationQuery, StatusTranslation>;

const relationStateConfig = {
	ACCEPTED: {
		text: "enums.relation_state.accepted",
		group: "status.groupCompleted",
		color: "success",
	},
	BANNED: {
		text: "enums.relation_state.banned",
		group: "status.groupFailed",
		color: "error",
	},
	PENDING: {
		text: "enums.relation_state.pending",
		group: "status.groupProcessing",
		color: "warning",
	},
	REJECTED: {
		text: "enums.relation_state.rejected",
		group: "status.groupFailed",
		color: "error",
	},
} as const satisfies Record<RelationState, StatusTranslation>;

const paymentStateConfig = {
	accepted: {
		text: "enums.payment_state.accepted",
		group: "status.groupCompleted",
		color: "success",
	},
	rejected: {
		text: "enums.payment_state.rejected",
		group: "status.groupFailed",
		color: "error",
	},
	pending: {
		text: "enums.payment_state.pending",
		group: "status.groupProcessing",
		color: "warning",
	},
} as const satisfies Record<PaymentState, StatusTranslation>;

const senderPriorityConfig = {
	EXCLUDED: {
		text: "enums.sender_priority.excluded",
		group: "status.groupCompleted",
		color: "primary",
	},
	LOW: {
		text: "enums.sender_priority.low",
		group: "status.groupFailed",
		color: "success",
	},
	MEDIUM: {
		text: "enums.sender_priority.medium",
		group: "status.groupProcessing",
		color: "warning",
	},
	HIGH: {
		text: "enums.sender_priority.high",
		group: "status.groupProcessing",
		color: "error",
	},
	VERY_HIGH: {
		text: "enums.sender_priority.very_high",
		group: "status.groupProcessing",
		color: "error",
	},
} as const satisfies Record<SenderPriorityUsage, StatusTranslation>;

export function useOrderStatusTranslator(t: TranslationF) {
	const { orderStatusMap } = useStatusMap();
	return (status: OrderStatus): StatusTranslation => {
		const cfg = orderStatusConfig[status];
		return {
			text: t(cfg.text) as TranslationKey,
			helper: t(cfg.helper) as TranslationKey,
			group: t(cfg.group),
			color: orderStatusMap[status]
				? categoryColors[orderStatusMap[status].category]
				: cfg.color,
		};
	};
}

export function getTransferRecordStatusTranslation(
	status: TransferRecordState,
	t: TranslationF,
): StatusTranslation {
	const cfg = transferRecordStatusConfig[status];
	return {
		text: t(cfg.text) as TranslationKey,
		helper: t(cfg.helper) as TranslationKey,
		group: t(cfg.group),
		color: cfg.color,
	};
}

export function useSenderStatusTranslator(t: TranslationF) {
	const { senderStatusMap } = useStatusMap();

	return (status: SenderStatus): StatusTranslation => {
		const cfg = senderStatusConfig[status];

		return {
			text: t(cfg.text) as TranslationKey,
			group: t(cfg.group),
			color: senderStatusMap[status]
				? categoryColors[senderStatusMap[status].category]
				: cfg.color,
		};
	};
}

export function getRelationQueryTranslation(
	status: RelationQuery,
	t: TranslationF,
): StatusTranslation {
	const cfg = relationQueryConfig[status];
	return {
		text: t(cfg.text) as TranslationKey,
		group: t(cfg.group),
		color: cfg.color,
	};
}

export function getRelationStateTranslation(
	status: RelationState,
	t: TranslationF,
): StatusTranslation {
	const cfg = relationStateConfig[status];
	return {
		text: t(cfg.text) as TranslationKey,
		group: t(cfg.group),
		color: cfg.color,
	};
}

export function getPaymentStateTranslation(
	status: PaymentState,
	t: TranslationF,
): StatusTranslation {
	const cfg = paymentStateConfig[status];
	return {
		text: t(cfg.text) as TranslationKey,
		group: t(cfg.group),
		color: cfg.color,
	};
}

export function getSenderPriorityTranslation(
	status: SenderPriorityUsage,
	t: TranslationF,
): StatusTranslation {
	const cfg = senderPriorityConfig[status];
	return {
		text: t(cfg.text) as TranslationKey,
		group: t(cfg.group),
		color: cfg.color,
	};
}
